import React from 'react';
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import {useParams} from "react-router-dom";
import {withFirebase} from '../Firebase';
import {AuthUserContext, withAuthorization} from '../Session';
import 'react-tabs/style/react-tabs.css';
import './aiAdvisor.css';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';

// Companies with AI Advisor enabled
const AI_ENABLED_COMPANIES = [
    'jLDiu0jZ8nVdWUi2sslOqB9dDrL2', // Gold Gondola
    'rftyEcoF57d5H2gD0Rcqqa2ngV63', // Metalac
    'TIu37v7eDqUdrzqU05Eh6YNYmbG2', // Style
    'T3TmSw6AZpdMo86NeDHMHUMzkmM2', // NOA Pet World
];

const AIAdvisorPage = ({fakeUid}) => {
    const {uid} = useParams();
    return (
        <AuthUserContext.Consumer>
            {authUser => {
                const effectiveUid = fakeUid || authUser.uid;
                const isEnabled = AI_ENABLED_COMPANIES.includes(effectiveUid);

                return (
                    <div className="min-h-screen bg-gray-50">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                            {/* Page Header */}
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold text-gray-900">AI Savetnik</h1>
                                <p className="text-gray-600 mt-1">Pametni predlozi za povecanje lojalnosti kupaca</p>
                            </div>
                            {isEnabled ? (
                                <AIAdvisorContent authUser={authUser} uid={uid} fakeUid={fakeUid}/>
                            ) : (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                                    <h3 className="text-lg font-semibold text-yellow-800 mb-2">AI Savetnik nije aktiviran</h3>
                                    <p className="text-yellow-700">
                                        AI Savetnik trenutno nije dostupan za vasu kompaniju.
                                        Kontaktirajte nas za aktivaciju ove funkcionalnosti.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }}
        </AuthUserContext.Consumer>
    )
};

let useUID;

class AIAdvisorContentBase extends React.Component {
    constructor(props) {
        super(props);
        if (this.props.fakeUid) {
            useUID = this.props.fakeUid;
        } else {
            useUID = this.props.authUser.uid;
        }

        this.state = {
            company: null,
            loading: false,
            generating: false,
            executing: false,
            suggestions: [],
            customInstructions: '',
            automationRules: [],
            aiSummary: null,
            showDataCriteria: false, // Toggle for showing AI criteria explanation
            showPromptPreview: false, // Toggle for showing AI prompt preview
            selectedTabIndex: 0,
            error: null,
            // Preview modal state
            showPreview: false,
            previewSuggestion: null,
            editedOffer: '',
            editedImage: '',
            // Target users state
            targetUsers: [],
            loadingUsers: false,
            showAllUsers: false,
            editedUids: '', // Custom UIDs (comma-separated)
            useCustomUids: false, // Toggle for custom UIDs mode
            usedOnlyOnce: true, // Campaign can only be used once per user (default true)
            // Edit rule modal state
            showEditRule: false,
            editingRule: null,
            editRuleOffer: '',
            editRuleImage: '',
            savingRule: false,
            // Image upload state
            uploadingImage: false,
            uploadingRuleImage: false,
            // Company logo URL
            companyLogoURL: '',
            // Cooldown configuration
            cooldownConfig: {
                defaults: {
                    birthday_gift: 365,
                    reactivation: 30,
                    reward: 30,
                    promotion: 7,
                    seasonal: 14,
                    follower_engagement: 7
                },
                fallbackCooldown: 14
            },
            savingCooldowns: false
        };

        this.generateSuggestions = this.generateSuggestions.bind(this);
        this.executeSuggestion = this.executeSuggestion.bind(this);
        this.dismissSuggestion = this.dismissSuggestion.bind(this);
        this.saveCustomInstructions = this.saveCustomInstructions.bind(this);
        this.toggleAutomation = this.toggleAutomation.bind(this);
        this.openPreview = this.openPreview.bind(this);
        this.closePreview = this.closePreview.bind(this);
        this.confirmAndExecute = this.confirmAndExecute.bind(this);
        this.saveCooldownConfig = this.saveCooldownConfig.bind(this);
        this.updateCooldownValue = this.updateCooldownValue.bind(this);
        this.openEditRule = this.openEditRule.bind(this);
        this.closeEditRule = this.closeEditRule.bind(this);
        this.saveEditedRule = this.saveEditedRule.bind(this);
        this.handleImageUpload = this.handleImageUpload.bind(this);
        this.handleRuleImageUpload = this.handleRuleImageUpload.bind(this);
        this.getCompanyLogoUrl = this.getCompanyLogoUrl.bind(this);
    }

    componentDidMount() {
        this.setState({loading: true});

        // Fetch company data
        this.companyUnsub = this.props.firebase.company(useUID).onSnapshot(docSnapshot => {
            this.setState({
                company: docSnapshot.data()
            });
        }, err => {
            console.log(`Encountered error: ${err}`);
        });

        // Fetch AI suggestions
        this.fetchSuggestions();

        // Fetch custom instructions
        this.fetchCustomInstructions();

        // Fetch automation rules
        this.fetchAutomationRules();

        // Fetch AI summary
        this.fetchAISummary();

        // Fetch cooldown configuration
        this.fetchCooldownConfig();

        // Fetch company logo URL
        this.getCompanyLogoUrl();

        this.setState({loading: false});
    }

    getCompanyLogoUrl() {
        this.props.firebase.storageReference('/Inc')
            .child(`${useUID}.png`)
            .getDownloadURL()
            .then(url => {
                this.setState({companyLogoURL: url});
            })
            .catch(err => console.error("Error retrieving Logo URL: ", err));
    }

    componentWillUnmount() {
        if (this.companyUnsub) this.companyUnsub();
        if (this.suggestionsUnsub) this.suggestionsUnsub();
        if (this.rulesUnsub) this.rulesUnsub();
    }

    fetchSuggestions() {
        this.suggestionsUnsub = this.props.firebase.aiSuggestions(useUID)
            .onSnapshot(snapshot => {
                const suggestions = [];
                snapshot.forEach(doc => {
                    suggestions.push({id: doc.id, ...doc.data()});
                });
                this.setState({suggestions});
            });
    }

    fetchCustomInstructions() {
        this.props.firebase.aiSettings(useUID, 'customInstructions')
            .get()
            .then(doc => {
                if (doc.exists) {
                    this.setState({customInstructions: doc.data().instructions || ''});
                }
            });
    }

    fetchAutomationRules() {
        this.rulesUnsub = this.props.firebase.aiAutomationRules(useUID)
            .onSnapshot(snapshot => {
                const automationRules = [];
                snapshot.forEach(doc => {
                    automationRules.push({id: doc.id, ...doc.data()});
                });
                this.setState({automationRules});
            });
    }

    fetchAISummary() {
        this.props.firebase.aiSettings(useUID, 'summary')
            .get()
            .then(doc => {
                if (doc.exists) {
                    this.setState({aiSummary: doc.data()});
                }
            });
    }

    fetchCooldownConfig() {
        this.props.firebase.aiSettings(useUID, 'campaignCooldowns')
            .get()
            .then(doc => {
                if (doc.exists) {
                    const data = doc.data();
                    this.setState({
                        cooldownConfig: {
                            defaults: data.defaults || this.state.cooldownConfig.defaults,
                            fallbackCooldown: data.fallbackCooldown || 14
                        }
                    });
                }
            });
    }

    updateCooldownValue(campaignType, value) {
        const numValue = parseInt(value) || 0;
        this.setState(prevState => ({
            cooldownConfig: {
                ...prevState.cooldownConfig,
                defaults: {
                    ...prevState.cooldownConfig.defaults,
                    [campaignType]: numValue
                }
            }
        }));
    }

    saveCooldownConfig() {
        this.setState({savingCooldowns: true});

        this.props.firebase.aiSettings(useUID, 'campaignCooldowns')
            .set({
                defaults: this.state.cooldownConfig.defaults,
                fallbackCooldown: this.state.cooldownConfig.fallbackCooldown,
                updatedAt: new Date()
            }, {merge: true})
            .then(() => {
                alert('Podesavanja sacuvana!');
                this.setState({savingCooldowns: false});
            })
            .catch(err => {
                console.error('Error saving cooldown config:', err);
                alert('Greska pri cuvanju podesavanja.');
                this.setState({savingCooldowns: false});
            });
    }

    getCampaignTypeLabel(type) {
        const labels = {
            birthday_gift: 'Rodendanski poklon',
            reactivation: 'Reaktivacija',
            reward: 'Nagrada za lojalnost',
            promotion: 'Promocija',
            seasonal: 'Sezonska ponuda',
            follower_engagement: 'Angazovanje pratilaca'
        };
        return labels[type] || type;
    }

    openEditRule(rule) {
        this.setState({
            showEditRule: true,
            editingRule: rule,
            editRuleOffer: rule.template?.offerValue || '',
            editRuleImage: rule.template?.image || ''
        });
    }

    closeEditRule() {
        this.setState({
            showEditRule: false,
            editingRule: null,
            editRuleOffer: '',
            editRuleImage: '',
            savingRule: false
        });
    }

    saveEditedRule() {
        const {editingRule, editRuleOffer, editRuleImage} = this.state;
        if (!editingRule) return;

        this.setState({savingRule: true});

        this.props.firebase.aiAutomationRule(useUID, editingRule.id)
            .update({
                'template.offerValue': editRuleOffer,
                'template.image': editRuleImage || null,
                updatedAt: new Date()
            })
            .then(() => {
                alert('Pravilo uspešno ažurirano!');
                this.closeEditRule();
            })
            .catch(err => {
                console.error('Error updating rule:', err);
                alert('Greška pri čuvanju pravila.');
                this.setState({savingRule: false});
            });
    }

    handleImageUpload(event) {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Molimo izaberite sliku.');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('Slika je prevelika. Maksimalna veličina je 5MB.');
            return;
        }

        this.setState({uploadingImage: true});

        const now = Date.now();
        const imagePath = `/${useUID}/ai_campaigns/${useUID}_${now}_${file.name}`;

        this.props.firebase.storageReference(imagePath)
            .put(file)
            .then(() => {
                return this.props.firebase.storageReference(imagePath).getDownloadURL();
            })
            .then(url => {
                this.setState({
                    editedImage: url,
                    uploadingImage: false
                });
            })
            .catch(err => {
                console.error('Error uploading image:', err);
                alert('Greška pri uploadu slike.');
                this.setState({uploadingImage: false});
            });
    }

    handleRuleImageUpload(event) {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Molimo izaberite sliku.');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('Slika je prevelika. Maksimalna veličina je 5MB.');
            return;
        }

        this.setState({uploadingRuleImage: true});

        const now = Date.now();
        const imagePath = `/${useUID}/ai_campaigns/${useUID}_${now}_${file.name}`;

        this.props.firebase.storageReference(imagePath)
            .put(file)
            .then(() => {
                return this.props.firebase.storageReference(imagePath).getDownloadURL();
            })
            .then(url => {
                this.setState({
                    editRuleImage: url,
                    uploadingRuleImage: false
                });
            })
            .catch(err => {
                console.error('Error uploading image:', err);
                alert('Greška pri uploadu slike.');
                this.setState({uploadingRuleImage: false});
            });
    }

// Build prompt preview (mirrors backend buildContext function)
    buildPromptPreview() {
        const { company, aiSummary, customInstructions } = this.state;

        if (!company || !aiSummary) {
            return 'Podaci nisu dostupni. Sačekajte da se učitaju.';
        }

        const now = new Date();
        const monthNames = ['januar', 'februar', 'mart', 'april', 'maj', 'jun',
                            'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'];
        const dayNames = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];

        let prompt = `Ti si AI savetnik za program lojalnosti kompanije "${company.name}".
Tvoj zadatak je da analiziraš podatke o kupcima i predložiš KONKRETNE strategije za povećanje lojalnosti i prodaje.

## PODACI O KOMPANIJI
- Naziv: ${company.name}
- Tip biznisa: ${company.billOptionsType || 'retail'}

## STATISTIKA KUPACA (poslednjih 90 dana)

### Osnovni podaci
- Ukupan broj kupaca: ${aiSummary.totalCustomers ?? 'N/A'}
- Ukupan broj transakcija: ${aiSummary.totalScans90Days ?? 'N/A'}
- Ukupna vrednost: ${aiSummary.totalValue90Days?.toLocaleString('sr-RS') ?? 'N/A'} RSD
- Prosečna vrednost po transakciji: ${aiSummary.avgValuePerScan ?? 'N/A'} RSD

### Segmentacija kupaca
- Novi kupci (1-2 posete): ${aiSummary.segments?.new ?? 0}
- Povratnici (3-9 poseta): ${aiSummary.segments?.returning ?? 0}
- Lojalni kupci (10+ poseta): ${aiSummary.segments?.loyal ?? 0}
- Neaktivni (>60 dana): ${aiSummary.inactiveCustomers ?? 0}
- Pratioci bez kupovine: ${aiSummary.segments?.followersOnly ?? 0}

### Prilike
- Kupci sa predstojećim rođendanima (7 dana): ${aiSummary.upcomingBirthdays ?? 0}
- Pratioci za konverziju: ${aiSummary.segments?.followersOnly ?? 0}`;

        if (aiSummary.visitPatterns) {
            prompt += `

### Vremenski obrasci poseta
- Najprometniji dani: ${aiSummary.visitPatterns.topDays ?? 'N/A'}
- Najprometniji sati: ${aiSummary.visitPatterns.topHours ?? 'N/A'}
- Vikend vs radni dani: ${aiSummary.visitPatterns.weekendRatio ?? 'N/A'}% vikend`;
        }

        if (aiSummary.valueSegments) {
            prompt += `

### Segmentacija po vrednosti
- VIP kupci (top 10%): ${aiSummary.valueSegments.vipCount ?? 0} kupaca, ${aiSummary.valueSegments.vipRevenue ?? 0}% prometa
- Prosečni kupci: ${aiSummary.valueSegments.averageCount ?? 0} kupaca
- Kupci male vrednosti: ${aiSummary.valueSegments.lowCount ?? 0} kupaca`;
        }

        if (aiSummary.churnAnalysis) {
            prompt += `

### Analiza odliva kupaca
- Prosečan broj dana između poseta: ${aiSummary.churnAnalysis.avgDaysBetweenVisits ?? 'N/A'}
- Kupci u riziku od odliva (bez posete 30-60 dana): ${aiSummary.churnAnalysis.atRiskCount ?? 0}
- Izgubljeni kupci (>90 dana): ${aiSummary.churnAnalysis.lostCount ?? 0}`;
        }

        if (aiSummary.campaignHistory) {
            prompt += `

### Istorija kampanja (poslednjih 30 dana)
- Broj kampanja: ${aiSummary.campaignHistory.total ?? 0}
- Prosečna stopa iskorišćenosti: ${((aiSummary.campaignHistory.avgUsageRate ?? 0) * 100).toFixed(1)}%
- Prosečna stopa lajkovanja: ${((aiSummary.campaignHistory.avgLikeRate ?? 0) * 100).toFixed(1)}%
- Ukupno lajkova: ${aiSummary.campaignHistory.totalLikes ?? 0}
- Najefikasniji tip kampanje (po iskorišćenosti): ${aiSummary.campaignHistory.bestType ?? 'N/A'}
- Najpopularniji tip kampanje (po lajkovima): ${aiSummary.campaignHistory.mostLikedType ?? 'N/A'}`;
        }

        if (aiSummary.trends) {
            prompt += `

### Trendovi
- Promena broja transakcija (vs prethodni mesec): ${aiSummary.trends.scansTrend ?? 'N/A'}%
- Promena vrednosti (vs prethodni mesec): ${aiSummary.trends.valueTrend ?? 'N/A'}%`;
        }

        prompt += `

## TRENUTNI KONTEKST
- Današnji datum: ${now.getDate()}. ${monthNames[now.getMonth()]} ${now.getFullYear()}.
- Dan u nedelji: ${dayNames[now.getDay()]}`;

        if (aiSummary.recentCampaignsList && aiSummary.recentCampaignsList.length > 0) {
            prompt += `

## ISTORIJA POSLATIH KAMPANJA (poslednjih 30 dana)
NE predlaži slične akcije ako su već poslate nedavno!
Lajkovi ukazuju na popularnost kampanje kod korisnika.

${aiSummary.recentCampaignsList.map(c => {
    const stats = [`${c.userCount} korisnika`];
    if (c.used !== undefined) stats.push(`${c.used} iskoristilo`);
    if (c.likes !== undefined) stats.push(`${c.likes} lajkova`);
    return `- ${c.date}: "${c.offer}" (${c.type}) - ${stats.join(', ')}`;
}).join('\n')}`;
        }

        if (customInstructions) {
            prompt += `

## POSEBNE INSTRUKCIJE OD BIZNISA
${customInstructions}`;
        }

        prompt += `

## TVOJ ZADATAK

Na osnovu svih podataka iznad, predloži 3-5 KONKRETNIH akcija za povećanje lojalnosti i prodaje.

Za svaku akciju navedi:
- type: Tip akcije (birthday_gift, reactivation, reward, promotion, seasonal, win_back, vip_exclusive, flash_sale, follower_engagement...)
- category: "recurring" (ponavljajuća) ili "one_time" (jednokratna)
- title: Kratak naslov
- description: Zašto predlažeš ovu akciju
- suggestedOffer: Tekst poruke za kupca
- estimatedUsers: Procenjen broj kupaca
- targeting: Uslovi za ciljanje kupaca
- priority: high/medium/low
- expectedROI: Procena očekivanog povrata`;

        return prompt;
    }

    async generateSuggestions() {
        this.setState({generating: true, error: null});

        try {
            const idToken = await this.props.firebase.auth.currentUser.getIdToken(false);

            const response = await fetch(
                'https://europe-west3-digitalna-kartica.cloudfunctions.net/generateAISuggestions',
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${idToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        companyUid: useUID,
                        customInstructions: this.state.customInstructions
                    })
                }
            );

            if (!response.ok) {
                throw new Error('Failed to generate suggestions');
            }

            const data = await response.json();
            console.log('AI suggestions generated:', data);

        } catch (error) {
            console.error('Error generating suggestions:', error);
            this.setState({error: 'Greska pri generisanju predloga. Pokusajte ponovo.'});
        } finally {
            this.setState({generating: false});
        }
    }

    async executeSuggestion(suggestion, customOffer = null, customImage = null, customUids = null, usedOnlyOnce = true) {
        this.setState({executing: true});
        try {
            const idToken = await this.props.firebase.auth.currentUser.getIdToken(false);

            const response = await fetch(
                'https://europe-west3-digitalna-kartica.cloudfunctions.net/executeAISuggestion',
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${idToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        companyUid: useUID,
                        suggestionId: suggestion.id,
                        customOffer: customOffer,
                        customImage: customImage,
                        customUids: customUids,
                        usedOnlyOnce: usedOnlyOnce
                    })
                }
            );

            if (!response.ok) {
                throw new Error('Failed to execute suggestion');
            }

            alert('Kampanja uspesno kreirana!');
            this.closePreview();

        } catch (error) {
            console.error('Error executing suggestion:', error);
            alert('Greska pri kreiranju kampanje.');
        } finally {
            this.setState({executing: false});
        }
    }

    dismissSuggestion(suggestionId) {
        this.props.firebase.aiSuggestion(useUID, suggestionId)
            .update({status: 'dismissed'});
    }

    async openPreview(suggestion) {
        const {companyLogoURL} = this.state;
        // Default image is company logo
        const defaultImage = companyLogoURL || '';

        this.setState({
            showPreview: true,
            previewSuggestion: suggestion,
            editedOffer: suggestion.suggestedOffer || '',
            editedImage: defaultImage,
            targetUsers: [],
            loadingUsers: true,
            showAllUsers: false,
            editedUids: '',
            useCustomUids: false
        });

        // Fetch target users
        try {
            const idToken = await this.props.firebase.auth.currentUser.getIdToken(false);
            const response = await fetch(
                'https://europe-west3-digitalna-kartica.cloudfunctions.net/getAISuggestionTargetUsers',
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${idToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        companyUid: useUID,
                        suggestionId: suggestion.id
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();
                const users = data.users || [];
                // Set editedUids to the UIDs from the query
                const uidsString = users.map(u => u.uid).join(', ');
                this.setState({
                    targetUsers: users,
                    loadingUsers: false,
                    editedUids: uidsString
                });
            } else {
                this.setState({loadingUsers: false});
            }
        } catch (error) {
            console.error('Error fetching target users:', error);
            this.setState({loadingUsers: false});
        }
    }

    closePreview() {
        this.setState({
            showPreview: false,
            previewSuggestion: null,
            editedOffer: '',
            editedImage: '',
            targetUsers: [],
            loadingUsers: false,
            showAllUsers: false,
            editedUids: '',
            useCustomUids: false,
            usedOnlyOnce: true
        });
    }

    confirmAndExecute() {
        const {previewSuggestion, editedOffer, editedImage, useCustomUids, editedUids, usedOnlyOnce} = this.state;
        if (previewSuggestion) {
            // Parse custom UIDs if enabled
            let customUids = null;
            if (useCustomUids && editedUids.trim()) {
                customUids = editedUids.split(',')
                    .map(uid => uid.trim())
                    .filter(uid => uid.length > 0);

                if (customUids.length === 0) {
                    alert('Molimo unesite bar jedan UID.');
                    return;
                }
            }
            this.executeSuggestion(previewSuggestion, editedOffer, editedImage, customUids, usedOnlyOnce);
        }
    }

    saveCustomInstructions() {
        this.props.firebase.aiSettings(useUID, 'customInstructions')
            .set({
                instructions: this.state.customInstructions,
                updatedAt: new Date()
            })
            .then(() => {
                alert('Instrukcije sacuvane!');
            });
    }

    toggleAutomation(suggestion, enabled) {
        if (enabled) {
            // Determine activePeriod based on suggestion type
            const activePeriod = this.getDefaultActivePeriod(suggestion);

            // Create automation rule
            this.props.firebase.aiAutomationRules(useUID)
                .add({
                    type: suggestion.type,
                    name: `Auto: ${suggestion.title}`,
                    enabled: true,
                    targeting: suggestion.targeting,
                    template: {
                        offerValue: suggestion.suggestedOffer,
                        campaignType: suggestion.type
                    },
                    activePeriod: activePeriod,
                    createdAt: new Date(),
                    createdFromSuggestionId: suggestion.id
                });

            // Update suggestion status
            this.props.firebase.aiSuggestion(useUID, suggestion.id)
                .update({status: 'automated'});
        }
    }

    getDefaultActivePeriod(suggestion) {
        // For birthday gifts and reactivation - always active
        if (suggestion.type === 'birthday_gift' || suggestion.type === 'reactivation') {
            return { type: 'always' };
        }

        // For reward - always active (loyal customers)
        if (suggestion.type === 'reward') {
            return { type: 'always' };
        }

        // For promotion - weekly (e.g., every Monday)
        if (suggestion.type === 'promotion') {
            return {
                type: 'weekly',
                daysOfWeek: [1] // Monday by default
            };
        }

        // For seasonal - try to detect date from title
        if (suggestion.type === 'seasonal') {
            const title = suggestion.title?.toLowerCase() || '';

            // 8. mart
            if (title.includes('8. mart') || title.includes('dan žena') || title.includes('dan zena')) {
                return {
                    type: 'yearly',
                    startMonth: 3, startDay: 1,
                    endMonth: 3, endDay: 8
                };
            }

            // Valentine's Day
            if (title.includes('valentin') || title.includes('zaljubljenih')) {
                return {
                    type: 'yearly',
                    startMonth: 2, startDay: 7,
                    endMonth: 2, endDay: 14
                };
            }

            // Christmas / New Year
            if (title.includes('božić') || title.includes('bozic') || title.includes('nova godina')) {
                return {
                    type: 'yearly',
                    startMonth: 12, startDay: 20,
                    endMonth: 1, endDay: 7
                };
            }

            // Black Friday
            if (title.includes('black friday')) {
                return {
                    type: 'yearly',
                    startMonth: 11, startDay: 20,
                    endMonth: 11, endDay: 30
                };
            }

            // Easter - approximate (varies each year)
            if (title.includes('uskrs')) {
                return {
                    type: 'yearly',
                    startMonth: 4, startDay: 1,
                    endMonth: 5, endDay: 5
                };
            }

            // Default for unknown seasonal: always (can be edited manually)
            return { type: 'always' };
        }

        return { type: 'always' };
    }

    render() {
        const {
            company,
            loading,
            generating,
            executing,
            suggestions,
            customInstructions,
            automationRules,
            aiSummary,
            error,
            showPreview,
            previewSuggestion,
            editedOffer,
            editedImage,
            targetUsers,
            loadingUsers,
            showAllUsers,
            editedUids,
            useCustomUids,
            showEditRule,
            editingRule,
            editRuleOffer,
            editRuleImage,
            savingRule,
            uploadingImage,
            uploadingRuleImage,
            companyLogoURL
        } = this.state;

        const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
        const executedSuggestions = suggestions.filter(s => s.status === 'executed');

        return (
            <Tabs selectedIndex={this.state.selectedTabIndex}
                  onSelect={index => this.setState({selectedTabIndex: index})}>
                <TabList>
                    <Tab>Predlozi ({pendingSuggestions.length})</Tab>
                    <Tab>Automatizacija ({automationRules.length})</Tab>
                    <Tab>Podesavanja</Tab>
                    <Tab>Istorija ({executedSuggestions.length})</Tab>
                </TabList>

                {/* Suggestions Tab */}
                <TabPanel>
                    <div className="ai-advisor-section">
                        {/* Data Aggregation Info */}
                        {aiSummary && aiSummary.lastUpdated && (
                            <div className="ai-data-info-container">
                                <div className="ai-data-info">
                                    <svg className="ai-data-info-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="ai-data-info-label">Agregirani podaci</span>
                                    <span className="ai-data-info-date">
                                        (ažurirano: {aiSummary.lastUpdated.toDate ?
                                            new Date(aiSummary.lastUpdated.toDate()).toLocaleDateString('sr-RS', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }) : 'N/A'})
                                    </span>
                                    <button
                                        className="ai-data-info-toggle"
                                        onClick={() => this.setState({ showDataCriteria: !this.state.showDataCriteria })}
                                    >
                                        {this.state.showDataCriteria ? 'Sakrij detalje' : 'Prikaži šta AI analizira'}
                                        <svg
                                            className={`ai-data-info-chevron ${this.state.showDataCriteria ? 'expanded' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Expandable AI Criteria Explanation */}
                                {this.state.showDataCriteria && (
                                    <div className="ai-criteria-details">
                                        <div className="ai-criteria-grid">
                                            <div className="ai-criteria-item">
                                                <h4>Osnovni podaci</h4>
                                                <ul>
                                                    <li><strong>Ukupno kupaca:</strong> {aiSummary.totalCustomers ?? 'N/A'}</li>
                                                    <li><strong>Transakcija (90 dana):</strong> {aiSummary.totalScans90Days ?? 'N/A'}</li>
                                                    <li><strong>Ukupna vrednost:</strong> {aiSummary.totalValue90Days?.toLocaleString('sr-RS') ?? 'N/A'} RSD</li>
                                                    <li><strong>Prosečna potrošnja:</strong> {aiSummary.avgValuePerScan ?? 'N/A'} RSD</li>
                                                </ul>
                                            </div>

                                            <div className="ai-criteria-item">
                                                <h4>Segmentacija kupaca</h4>
                                                <ul>
                                                    <li><strong>Novi (1-2 posete):</strong> {aiSummary.segments?.new ?? 'N/A'}</li>
                                                    <li><strong>Povratnici (3-9):</strong> {aiSummary.segments?.returning ?? 'N/A'}</li>
                                                    <li><strong>Lojalni (10+):</strong> {aiSummary.segments?.loyal ?? 'N/A'}</li>
                                                    <li><strong>Samo pratioci:</strong> {aiSummary.segments?.followersOnly ?? 'N/A'}</li>
                                                    <li><strong>Neaktivni (60+ dana):</strong> {aiSummary.inactiveCustomers ?? 'N/A'}</li>
                                                    <li><strong>Predstojeći rođendani:</strong> {aiSummary.upcomingBirthdays ?? 'N/A'}</li>
                                                </ul>
                                            </div>

                                            <div className="ai-criteria-item">
                                                <h4>Obrasci poseta</h4>
                                                <ul>
                                                    <li><strong>Najprometniji dani:</strong> {aiSummary.visitPatterns?.topDays ?? 'N/A'}</li>
                                                    <li><strong>Najprometniji sati:</strong> {aiSummary.visitPatterns?.topHours ?? 'N/A'}</li>
                                                    <li><strong>Vikend posete:</strong> {aiSummary.visitPatterns?.weekendRatio ?? 'N/A'}%</li>
                                                </ul>
                                            </div>

                                            <div className="ai-criteria-item">
                                                <h4>Vrednosni segmenti</h4>
                                                <ul>
                                                    <li><strong>VIP kupci (top 10%):</strong> {aiSummary.valueSegments?.vipCount ?? 'N/A'}</li>
                                                    <li><strong>VIP udeo u prometu:</strong> {aiSummary.valueSegments?.vipRevenue ?? 'N/A'}%</li>
                                                    <li><strong>Prosečni kupci (60%):</strong> {aiSummary.valueSegments?.averageCount ?? 'N/A'}</li>
                                                    <li><strong>Kupci male vrednosti (30%):</strong> {aiSummary.valueSegments?.lowCount ?? 'N/A'}</li>
                                                </ul>
                                            </div>

                                            <div className="ai-criteria-item">
                                                <h4>Analiza odliva</h4>
                                                <ul>
                                                    <li><strong>Prosek dana između poseta:</strong> {aiSummary.churnAnalysis?.avgDaysBetweenVisits ?? 'N/A'}</li>
                                                    <li><strong>U riziku (30-60 dana):</strong> {aiSummary.churnAnalysis?.atRiskCount ?? 'N/A'}</li>
                                                    <li><strong>Izgubljeni (90+ dana):</strong> {aiSummary.churnAnalysis?.lostCount ?? 'N/A'}</li>
                                                </ul>
                                            </div>

                                            <div className="ai-criteria-item">
                                                <h4>Istorija kampanja (30 dana)</h4>
                                                <ul>
                                                    <li><strong>Broj kampanja:</strong> {aiSummary.campaignHistory?.total ?? 'N/A'}</li>
                                                    <li><strong>Prosečna iskorišćenost:</strong> {((aiSummary.campaignHistory?.avgUsageRate ?? 0) * 100).toFixed(1)}%</li>
                                                    <li><strong>Prosečna stopa lajkova:</strong> {((aiSummary.campaignHistory?.avgLikeRate ?? 0) * 100).toFixed(1)}%</li>
                                                    <li><strong>Ukupno lajkova:</strong> {aiSummary.campaignHistory?.totalLikes ?? 0}</li>
                                                    <li><strong>Najefikasniji tip:</strong> {aiSummary.campaignHistory?.bestType ?? 'N/A'}</li>
                                                    <li><strong>Najpopularniji tip:</strong> {aiSummary.campaignHistory?.mostLikedType ?? 'N/A'}</li>
                                                </ul>
                                            </div>

                                            {aiSummary.trends && (
                                                <div className="ai-criteria-item">
                                                    <h4>Trendovi (vs. prošli mesec)</h4>
                                                    <ul>
                                                        <li><strong>Promena transakcija:</strong> {aiSummary.trends?.scansTrend ?? 'N/A'}%</li>
                                                        <li><strong>Promena vrednosti:</strong> {aiSummary.trends?.valueTrend ?? 'N/A'}%</li>
                                                    </ul>
                                                </div>
                                            )}
                                        </div>

                                        <p className="ai-criteria-note">
                                            AI koristi ove podatke da generiše personalizovane predloge kampanja.
                                            Podaci se agregiraju 1. u mesecu ili na zahtev.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Company Info Header */}
                        {company && (
                            <div className="ai-company-header">
                                <h3>{company.name}</h3>
                                {aiSummary && (
                                    <div className="ai-summary-stats">
                                        <span>Kupaca: {aiSummary.totalCustomers ?? 'N/A'}</span>
                                        <span>Prosecna potrosnja: {aiSummary.avgValuePerScan ?? 'N/A'} RSD</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Generate Button */}
                        <div className="ai-actions">
                            <Button
                                variant="primary"
                                onClick={this.generateSuggestions}
                                disabled={generating}
                            >
                                {generating ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="mr-2" />
                                        Generisem predloge...
                                    </>
                                ) : (
                                    'Generisi nove predloge'
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => this.setState({ showPromptPreview: true })}
                                disabled={!aiSummary}
                                className="ml-2"
                            >
                                Prikaži prompt
                            </Button>
                        </div>

                        {/* Prompt Preview Modal */}
                        <Modal
                            show={this.state.showPromptPreview}
                            onHide={() => this.setState({ showPromptPreview: false })}
                            size="lg"
                            className="ai-prompt-modal"
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>AI Prompt - Pregled</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <p className="ai-prompt-description">
                                    Ovo je prompt koji se šalje AI-u za generisanje predloga kampanja.
                                    Sadrži sve agregirane podatke o vašim kupcima i trenutni kontekst.
                                </p>
                                <pre className="ai-prompt-content">
                                    {this.buildPromptPreview()}
                                </pre>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button
                                    variant="secondary"
                                    onClick={() => this.setState({ showPromptPreview: false })}
                                >
                                    Zatvori
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        this.setState({ showPromptPreview: false });
                                        this.generateSuggestions();
                                    }}
                                    disabled={generating}
                                >
                                    Generiši predloge
                                </Button>
                            </Modal.Footer>
                        </Modal>

                        {error && (
                            <div className="ai-error">{error}</div>
                        )}

                        {/* Suggestions List */}
                        <div className="suggestions-list">
                            {pendingSuggestions.length === 0 ? (
                                <div className="no-suggestions">
                                    <p>Nema aktivnih predloga.</p>
                                    <p>Kliknite "Generisi nove predloge" da dobijete AI preporuke.</p>
                                </div>
                            ) : (
                                pendingSuggestions.map(suggestion => (
                                    <SuggestionCard
                                        key={suggestion.id}
                                        suggestion={suggestion}
                                        onExecute={() => this.openPreview(suggestion)}
                                        onDismiss={() => this.dismissSuggestion(suggestion.id)}
                                        onToggleAutomation={(enabled) => this.toggleAutomation(suggestion, enabled)}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </TabPanel>

                {/* Automation Rules Tab */}
                <TabPanel>
                    <div className="ai-advisor-section">
                        <h3>Automatska pravila</h3>
                        <p className="section-description">
                            Pravila koja se automatski izvrsavaju svaki dan u 09:00.
                        </p>

                        {automationRules.length === 0 ? (
                            <div className="no-rules">
                                <p>Nema aktivnih pravila.</p>
                                <p>Ukljucite automatizaciju na nekom predlogu da kreirate pravilo.</p>
                            </div>
                        ) : (
                            <div className="rules-list">
                                {automationRules.map(rule => (
                                    <RuleCard
                                        key={rule.id}
                                        rule={rule}
                                        onToggle={() => {
                                            this.props.firebase.aiAutomationRule(useUID, rule.id)
                                                .update({enabled: !rule.enabled});
                                        }}
                                        onDelete={() => {
                                            if (window.confirm('Da li ste sigurni da zelite da obrisete ovo pravilo?')) {
                                                this.props.firebase.aiAutomationRule(useUID, rule.id).delete();
                                            }
                                        }}
                                        onEdit={() => this.openEditRule(rule)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </TabPanel>

                {/* Settings Tab */}
                <TabPanel>
                    <div className="ai-advisor-section">
                        <h3>Prilagodite AI savetnika</h3>
                        <p className="section-description">
                            Dodajte instrukcije koje ce AI koristiti pri generisanju predloga.
                        </p>

                        <div className="instructions-editor">
                            <label>Vase instrukcije:</label>
                            <textarea
                                value={customInstructions}
                                onChange={(e) => this.setState({customInstructions: e.target.value})}
                                placeholder="Npr:
- Fokusiraj se na promocije hrane za pse
- Nikad ne nuditi vise od 15% popusta
- Poruke pisati na srpskom jeziku
- Za rodjendane nuditi minimum 10% popusta"
                                rows={8}
                            />
                            <Button variant="success" onClick={this.saveCustomInstructions}>
                                Sacuvaj instrukcije
                            </Button>
                        </div>

                        {/* Cooldown Configuration */}
                        <div className="cooldown-settings">
                            <h4>Minimalni razmak izmedju kampanja</h4>
                            <p className="section-description">
                                Podesite koliko dana mora proci pre nego sto korisnik ponovo dobije istu vrstu kampanje.
                            </p>

                            <div className="cooldown-grid">
                                {Object.entries(this.state.cooldownConfig.defaults).map(([type, days]) => (
                                    <div key={type} className="cooldown-item">
                                        <label>{this.getCampaignTypeLabel(type)}</label>
                                        <div className="cooldown-input-group">
                                            <input
                                                type="number"
                                                min="1"
                                                max="365"
                                                value={days}
                                                onChange={(e) => this.updateCooldownValue(type, e.target.value)}
                                            />
                                            <span>dana</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="cooldown-item fallback-cooldown">
                                <label>Default za nove tipove kampanja</label>
                                <div className="cooldown-input-group">
                                    <input
                                        type="number"
                                        min="1"
                                        max="365"
                                        value={this.state.cooldownConfig.fallbackCooldown}
                                        onChange={(e) => this.setState(prevState => ({
                                            cooldownConfig: {
                                                ...prevState.cooldownConfig,
                                                fallbackCooldown: parseInt(e.target.value) || 14
                                            }
                                        }))}
                                    />
                                    <span>dana</span>
                                </div>
                            </div>

                            <Button
                                variant="success"
                                onClick={this.saveCooldownConfig}
                                disabled={this.state.savingCooldowns}
                            >
                                {this.state.savingCooldowns ? 'Cuvam...' : 'Sacuvaj podesavanja'}
                            </Button>
                        </div>
                    </div>
                </TabPanel>

                {/* History Tab */}
                <TabPanel>
                    <div className="ai-advisor-section">
                        <h3>Izvrseni predlozi</h3>

                        {executedSuggestions.length === 0 ? (
                            <div className="no-history">
                                <p>Nema izvrsenih predloga.</p>
                            </div>
                        ) : (
                            <div className="history-list">
                                {executedSuggestions.map(suggestion => (
                                    <div key={suggestion.id} className="history-card">
                                        <span className="history-type">{suggestion.type}</span>
                                        <h4>{suggestion.title}</h4>
                                        <p>{suggestion.description}</p>
                                        {suggestion.executedAt && (
                                            <span className="history-date">
                                                Izvrseno: {new Date(suggestion.executedAt.toDate()).toLocaleDateString('sr-RS')}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabPanel>

{/* Preview Modal */}
                <Modal show={showPreview} onHide={this.closePreview} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Pregled kampanje</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {previewSuggestion && (
                            <div className="preview-content">
                                <div className="preview-section">
                                    <label className="preview-label">Tip kampanje:</label>
                                    <p className="preview-value">{previewSuggestion.type}</p>
                                </div>
                                <div className="preview-section">
                                    <label className="preview-label">Naslov:</label>
                                    <p className="preview-value">{previewSuggestion.title}</p>
                                </div>
                                <div className="preview-section">
                                    <label className="preview-label">Opis:</label>
                                    <p className="preview-value">{previewSuggestion.description}</p>
                                </div>
                                <div className="preview-section">
                                    <label className="preview-label">Procenjen broj korisnika:</label>
                                    <p className="preview-value">~{previewSuggestion.estimatedUsers || 'N/A'} kupaca</p>
                                </div>

                                {/* Targeting Criteria in Preview */}
                                <PreviewTargetingInfo suggestion={previewSuggestion} />

                                <div className="preview-section">
                                    <Form.Group>
                                        <Form.Label className="preview-label">Poruka koja ce biti poslata:</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            value={editedOffer}
                                            onChange={(e) => this.setState({editedOffer: e.target.value})}
                                            placeholder="Unesite poruku za kupce..."
                                        />
                                        <Form.Text className="text-muted">
                                            Mozete izmeniti poruku pre slanja.
                                        </Form.Text>
                                    </Form.Group>
                                </div>

                                <div className="preview-section">
                                    <Form.Group>
                                        <Form.Label className="preview-label">Slika kampanje:</Form.Label>

                                        {/* Image Preview */}
                                        <div className="image-preview-container" style={{
                                            border: '2px dashed #dee2e6',
                                            borderRadius: '8px',
                                            padding: '20px',
                                            textAlign: 'center',
                                            marginBottom: '15px',
                                            backgroundColor: '#f8f9fa'
                                        }}>
                                            {editedImage ? (
                                                <img
                                                    src={editedImage}
                                                    alt="Preview"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '200px',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/200x150?text=Greška';
                                                    }}
                                                />
                                            ) : (
                                                <div style={{color: '#6c757d', padding: '40px 0'}}>
                                                    Nema slike
                                                </div>
                                            )}
                                        </div>

                                        {/* Upload Button */}
                                        <div className="d-flex gap-2 mb-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                disabled={uploadingImage}
                                                onClick={() => document.getElementById('campaign-image-upload').click()}
                                            >
                                                {uploadingImage ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-1" />
                                                        Uploadujem...
                                                    </>
                                                ) : (
                                                    'Uploaduj sliku'
                                                )}
                                            </Button>
                                            {editedImage && companyLogoURL && editedImage !== companyLogoURL && (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => this.setState({editedImage: companyLogoURL})}
                                                >
                                                    Vrati na logo
                                                </Button>
                                            )}
                                            <input
                                                id="campaign-image-upload"
                                                type="file"
                                                accept="image/*"
                                                style={{display: 'none'}}
                                                onChange={this.handleImageUpload}
                                            />
                                        </div>

                                        {/* URL Input */}
                                        <Form.Control
                                            type="text"
                                            value={editedImage}
                                            onChange={(e) => this.setState({editedImage: e.target.value})}
                                            placeholder="Ili unesite URL slike..."
                                            size="sm"
                                        />
                                        <Form.Text className="text-muted">
                                            Uploadujte sliku ili unesite URL direktno.
                                        </Form.Text>
                                    </Form.Group>
                                </div>

                                {/* Target Users Section */}
                                <div className="preview-section">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="preview-label mb-0">
                                            Korisnici koji ce primiti kampanju ({useCustomUids
                                                ? editedUids.split(',').filter(u => u.trim()).length
                                                : targetUsers.length}):
                                        </label>
                                        <Form.Check
                                            type="switch"
                                            id="custom-uids-switch"
                                            label="Izmeni listu"
                                            checked={useCustomUids}
                                            onChange={(e) => this.setState({useCustomUids: e.target.checked})}
                                        />
                                    </div>

                                    {useCustomUids ? (
                                        <div className="custom-uids-section">
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                value={editedUids}
                                                onChange={(e) => this.setState({editedUids: e.target.value})}
                                                placeholder="Unesite UID-ove razdvojene zarezom..."
                                                style={{fontFamily: 'monospace', fontSize: '12px'}}
                                            />
                                            <Form.Text className="text-muted">
                                                Unesite UID-ove razdvojene zarezom. Broj korisnika: {editedUids.split(',').filter(u => u.trim()).length}
                                            </Form.Text>
                                        </div>
                                    ) : loadingUsers ? (
                                        <div className="users-loading">
                                            <Spinner animation="border" size="sm" /> Ucitavam korisnike...
                                        </div>
                                    ) : targetUsers.length === 0 ? (
                                        <p className="preview-value text-muted">Nema korisnika koji odgovaraju kriterijumima.</p>
                                    ) : (
                                        <div className="target-users-container">
                                            <div className="target-users-list">
                                                {(showAllUsers ? targetUsers : targetUsers.slice(0, 5)).map((user, index) => {
                                                    const displayName = (user.name && user.name !== 'N/A')
                                                        ? user.name
                                                        : (user.email && user.email !== 'N/A')
                                                            ? user.email
                                                            : (user.phone || user.uid);
                                                    return (
                                                        <div key={user.uid || index} className="target-user-item">
                                                            <span className="user-name">{displayName}</span>
                                                            <span className="user-details">
                                                                Poseta: {user.visitCount} | Potrosnja: {Math.round(user.totalValue || 0)} RSD
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {targetUsers.length > 5 && (
                                                <Button
                                                    variant="link"
                                                    className="show-more-btn"
                                                    onClick={() => this.setState({showAllUsers: !showAllUsers})}
                                                >
                                                    {showAllUsers
                                                        ? `Prikazi manje`
                                                        : `Prikazi jos ${targetUsers.length - 5} korisnika`
                                                    }
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Campaign Options Section */}
                                <div className="preview-section">
                                    <Form.Check
                                        type="checkbox"
                                        id="used-only-once-checkbox"
                                        label="Samo jednom po korisniku"
                                        checked={this.state.usedOnlyOnce}
                                        onChange={(e) => this.setState({usedOnlyOnce: e.target.checked})}
                                    />
                                    <Form.Text className="text-muted">
                                        Ako je ukljuceno, svaki korisnik moze iskoristiti kampanju samo jednom.
                                    </Form.Text>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.closePreview}>
                            Odustani
                        </Button>
                        <Button
                            variant="success"
                            onClick={this.confirmAndExecute}
                            disabled={executing || !editedOffer.trim()}
                        >
                            {executing ? (
                                <>
                                    <Spinner animation="border" size="sm" className="mr-2" />
                                    Saljem...
                                </>
                            ) : (
                                'Potvrdi i posalji'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Edit Rule Modal */}
                <Modal show={showEditRule} onHide={this.closeEditRule} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Izmeni pravilo automatizacije</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {editingRule && (
                            <div className="edit-rule-content">
                                <div className="preview-section">
                                    <label className="preview-label">Naziv pravila:</label>
                                    <p className="preview-value">{editingRule.name}</p>
                                </div>

                                <div className="preview-section">
                                    <Form.Group>
                                        <Form.Label className="preview-label">Poruka koja se salje:</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            value={editRuleOffer}
                                            onChange={(e) => this.setState({editRuleOffer: e.target.value})}
                                            placeholder="Unesite poruku za kupce..."
                                        />
                                    </Form.Group>
                                </div>

                                <div className="preview-section">
                                    <Form.Group>
                                        <Form.Label className="preview-label">Slika kampanje:</Form.Label>

                                        {/* Image Preview */}
                                        <div className="image-preview-container" style={{
                                            border: '2px dashed #dee2e6',
                                            borderRadius: '8px',
                                            padding: '20px',
                                            textAlign: 'center',
                                            marginBottom: '15px',
                                            backgroundColor: '#f8f9fa'
                                        }}>
                                            {editRuleImage ? (
                                                <img
                                                    src={editRuleImage}
                                                    alt="Preview"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '200px',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/200x150?text=Greška';
                                                    }}
                                                />
                                            ) : (
                                                <div style={{color: '#6c757d', padding: '40px 0'}}>
                                                    Nema slike (koristi se logo kompanije)
                                                </div>
                                            )}
                                        </div>

                                        {/* Upload Button */}
                                        <div className="d-flex gap-2 mb-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                disabled={uploadingRuleImage}
                                                onClick={() => document.getElementById('rule-image-upload').click()}
                                            >
                                                {uploadingRuleImage ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-1" />
                                                        Uploadujem...
                                                    </>
                                                ) : (
                                                    'Uploaduj sliku'
                                                )}
                                            </Button>
                                            {editRuleImage && (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => this.setState({editRuleImage: ''})}
                                                >
                                                    Ukloni sliku
                                                </Button>
                                            )}
                                            <input
                                                id="rule-image-upload"
                                                type="file"
                                                accept="image/*"
                                                style={{display: 'none'}}
                                                onChange={this.handleRuleImageUpload}
                                            />
                                        </div>

                                        {/* URL Input */}
                                        <Form.Control
                                            type="text"
                                            value={editRuleImage}
                                            onChange={(e) => this.setState({editRuleImage: e.target.value})}
                                            placeholder="Ili unesite URL slike..."
                                            size="sm"
                                        />
                                        <Form.Text className="text-muted">
                                            Uploadujte sliku ili unesite URL. Ostavite prazno za logo kompanije.
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.closeEditRule}>
                            Odustani
                        </Button>
                        <Button
                            variant="success"
                            onClick={this.saveEditedRule}
                            disabled={savingRule || !editRuleOffer.trim()}
                        >
                            {savingRule ? (
                                <>
                                    <Spinner animation="border" size="sm" className="mr-2" />
                                    Cuvam...
                                </>
                            ) : (
                                'Sacuvaj izmene'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Tabs>
        );
    }
}

// Helper function to generate targeting info and query
// Generate query code from dynamic conditions
const generateQueryFromConditions = (conditions) => {
    const companyPath = 'company/{companyUid}/visits';
    let queryCode = `// Dynamic Firestore Query
const visitsRef = db.collection('${companyPath}');
let query = visitsRef;

`;

    for (const condition of conditions) {
        if (condition.field === 'birthday' && condition.birthdayInDays !== undefined) {
            queryCode += `// Birthday: ${condition.birthdayInDays} dana unapred
// (posebna logika za izračunavanje)
`;
        } else if (condition.daysAgo !== undefined) {
            queryCode += `// ${condition.field} ${condition.operator} pre ${condition.daysAgo} dana
const cutoff_${condition.field} = new Date(Date.now() - ${condition.daysAgo} * 24 * 60 * 60 * 1000);
query = query.where('${condition.field}', '${condition.operator}', Timestamp.fromDate(cutoff_${condition.field}));
`;
        } else if (condition.daysAhead !== undefined) {
            queryCode += `// ${condition.field} ${condition.operator} za ${condition.daysAhead} dana
const cutoff_${condition.field} = new Date(Date.now() + ${condition.daysAhead} * 24 * 60 * 60 * 1000);
query = query.where('${condition.field}', '${condition.operator}', Timestamp.fromDate(cutoff_${condition.field}));
`;
        } else {
            queryCode += `query = query.where('${condition.field}', '${condition.operator}', ${condition.value});
`;
        }
    }

    queryCode += `
const snapshot = await query.get();

// + cooldown provera za spam zaštitu`;

    return queryCode;
};

// Get cooldown based on campaign type
const getCooldownForType = (type) => {
    const cooldowns = {
        'birthday_gift': '365 dana (jednom godišnje)',
        'reactivation': '30 dana (jednom mesečno)',
        'reward': '30 dana (jednom mesečno)',
        'promotion': '7 dana (jednom nedeljno)',
        'seasonal': '14 dana (jednom u 2 nedelje)',
        'follower_engagement': '7 dana (jednom nedeljno)'
    };
    return cooldowns[type] || '14 dana (default)';
};

const getTargetingInfo = (type, targeting = {}) => {
    // targeting has conditions array
    const conditions = targeting?.conditions || [];

    if (conditions.length === 0) {
        return {
            criteria: 'Nema definisanih uslova',
            description: 'Targeting nije konfigurisan.',
            cooldown: getCooldownForType(type),
            query: '// Nema uslova'
        };
    }

    // Generate criteria text from conditions
    const criteriaTexts = conditions.map(c => {
        const fieldNames = {
            'lastVisit': 'poslednja poseta',
            'count': 'broj poseta',
            'value': 'ukupna potrošnja',
            'birthday': 'rođendan',
            'firstVisit': 'prva poseta'
        };
        const fieldName = fieldNames[c.field] || c.field;

        if (c.birthdayInDays !== undefined) {
            return `rođendan u narednih ${c.birthdayInDays} dana`;
        } else if (c.daysAgo !== undefined) {
            return `${fieldName} ${c.operator} pre ${c.daysAgo} dana`;
        } else if (c.daysAhead !== undefined) {
            return `${fieldName} ${c.operator} za ${c.daysAhead} dana`;
        } else {
            return `${fieldName} ${c.operator} ${c.value}`;
        }
    });

    return {
        criteria: criteriaTexts.join(' + '),
        description: `Dinamički query sa ${conditions.length} uslov(a). Svi korisnici koji ispunjavaju uslove.`,
        cooldown: getCooldownForType(type),
        query: generateQueryFromConditions(conditions)
    };
};

// Suggestion Card Component
const SuggestionCard = ({suggestion, onExecute, onDismiss, onToggleAutomation}) => {
    const [showQuery, setShowQuery] = React.useState(false);

    const typeLabels = {
        'birthday_gift': 'Poklon za rodjendan',
        'reactivation': 'Reaktivacija',
        'reward': 'Nagrada',
        'promotion': 'Promocija',
        'seasonal': 'Sezonska ponuda',
        'win_back': 'Povratak kupaca',
        'vip_exclusive': 'VIP ekskluziva',
        'flash_sale': 'Flash rasprodaja',
        'referral': 'Preporuka',
        'feedback': 'Povratne informacije',
        'follower_engagement': 'Angazovanje pratilaca'
    };

    const isRecurring = suggestion.category === 'recurring';

    // Use database fields if available, fallback to hardcoded getTargetingInfo
    const fallbackInfo = getTargetingInfo(suggestion.type, suggestion.targeting);
    const targetingCriteria = suggestion.targetingQuery || fallbackInfo.criteria;
    const targetingDescription = suggestion.targetingDescription || fallbackInfo.description;
    const targetingCooldown = fallbackInfo.cooldown; // cooldown is always from config
    const targetingQueryCode = fallbackInfo.query; // technical query code

    return (
        <div className={`suggestion-card category-${suggestion.category || 'one_time'}`}>
            <div className="suggestion-header">
                <div className="suggestion-badges">
                    <span className={`suggestion-type type-${suggestion.type}`}>
                        {typeLabels[suggestion.type] || suggestion.type}
                    </span>
                    <span className={`suggestion-category ${isRecurring ? 'recurring' : 'one-time'}`}>
                        {isRecurring ? 'Ponavljajuci' : 'Jednokratni'}
                    </span>
                </div>
                {suggestion.estimatedUsers && (
                    <span className="suggestion-reach">
                        ~{suggestion.estimatedUsers} kupaca
                    </span>
                )}
            </div>

            <h4 className="suggestion-title">{suggestion.title}</h4>
            <p className="suggestion-description">{suggestion.description}</p>

            {/* Targeting Criteria */}
            <div className="suggestion-targeting">
                <div className="targeting-header">
                    <strong>Kriterijum ciljanja:</strong>
                    <Button
                        variant="link"
                        size="sm"
                        className="show-query-btn"
                        onClick={() => setShowQuery(!showQuery)}
                    >
                        {showQuery ? 'Sakrij query' : 'Prikazi query'}
                    </Button>
                </div>
                <p className="targeting-criteria">{targetingCriteria}</p>
                <p className="targeting-description">{targetingDescription}</p>
                {targetingCooldown && (
                    <p className="targeting-cooldown">
                        <span className="cooldown-icon">🔄</span> Minimalni razmak: <strong>{targetingCooldown}</strong>
                    </p>
                )}

                {showQuery && (
                    <pre className="targeting-query">{targetingQueryCode}</pre>
                )}
            </div>

            {suggestion.suggestedOffer && (
                <div className="suggestion-offer">
                    <strong>Predlozena poruka:</strong>
                    <p>{suggestion.suggestedOffer}</p>
                </div>
            )}

            <div className="suggestion-actions">
                <Button variant="success" onClick={onExecute}>
                    Kreiraj kampanju
                </Button>
                <Button variant="outline-secondary" onClick={onDismiss}>
                    Odbaci
                </Button>
                {isRecurring && (
                    <Button
                        variant="outline-primary"
                        onClick={() => onToggleAutomation(true)}
                        title="Ukljuci automatsko slanje ovog tipa kampanje"
                    >
                        Automatizuj
                    </Button>
                )}
            </div>
        </div>
    );
};

// Preview Targeting Info Component
const PreviewTargetingInfo = ({suggestion}) => {
    const [showQuery, setShowQuery] = React.useState(false);

    // Use database fields if available, fallback to hardcoded getTargetingInfo
    const fallbackInfo = getTargetingInfo(suggestion.type, suggestion.targeting);
    const targetingCriteria = suggestion.targetingQuery || fallbackInfo.criteria;
    const targetingDescription = suggestion.targetingDescription || fallbackInfo.description;
    const targetingCooldown = fallbackInfo.cooldown;
    const targetingQueryCode = fallbackInfo.query;

    return (
        <div className="preview-section">
            <div className="preview-targeting">
                <div className="targeting-header">
                    <label className="preview-label" style={{margin: 0}}>Kriterijum ciljanja:</label>
                    <Button
                        variant="link"
                        size="sm"
                        className="show-query-btn"
                        onClick={() => setShowQuery(!showQuery)}
                    >
                        {showQuery ? 'Sakrij query' : 'Prikazi query'}
                    </Button>
                </div>
                <p className="targeting-criteria">{targetingCriteria}</p>
                <p className="targeting-description">{targetingDescription}</p>
                {targetingCooldown && (
                    <p className="targeting-cooldown">
                        <span className="cooldown-icon">🔄</span> Minimalni razmak: <strong>{targetingCooldown}</strong>
                    </p>
                )}

                {showQuery && (
                    <pre className="targeting-query">{targetingQueryCode}</pre>
                )}
            </div>
        </div>
    );
};

// Helper function to format active period
const formatActivePeriod = (activePeriod) => {
    if (!activePeriod || !activePeriod.type || activePeriod.type === 'always') {
        return { label: 'Uvek aktivno', description: 'Pravilo se izvršava svaki dan' };
    }

    const monthNames = ['', 'januar', 'februar', 'mart', 'april', 'maj', 'jun',
                        'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'];
    const dayNames = ['nedelja', 'ponedeljak', 'utorak', 'sreda', 'četvrtak', 'petak', 'subota'];

    switch (activePeriod.type) {
        case 'yearly': {
            const start = `${activePeriod.startDay}. ${monthNames[activePeriod.startMonth]}`;
            const end = `${activePeriod.endDay}. ${monthNames[activePeriod.endMonth]}`;
            return {
                label: 'Godišnje',
                description: `Aktivno od ${start} do ${end}`
            };
        }
        case 'monthly': {
            const days = activePeriod.daysOfMonth?.join(', ') || '';
            return {
                label: 'Mesečno',
                description: `Aktivno ${days}. u mesecu`
            };
        }
        case 'weekly': {
            const days = activePeriod.daysOfWeek?.map(d => dayNames[d]).join(', ') || '';
            return {
                label: 'Nedeljno',
                description: `Aktivno: ${days}`
            };
        }
        default:
            return { label: 'Uvek aktivno', description: '' };
    }
};

// Rule Card Component
const RuleCard = ({rule, onToggle, onDelete, onEdit}) => {
    const [showQuery, setShowQuery] = React.useState(false);

    const typeLabels = {
        'birthday_gift': 'Poklon za rodjendan',
        'reactivation': 'Reaktivacija',
        'reward': 'Nagrada',
        'promotion': 'Promocija',
        'seasonal': 'Sezonska ponuda',
        'follower_engagement': 'Angazovanje pratilaca'
    };

    const targetingInfo = getTargetingInfo(rule.type, rule.targeting);
    const activePeriodInfo = formatActivePeriod(rule.activePeriod);

    return (
        <div className="rule-card">
            <div className="rule-header">
                <span className={`rule-status ${rule.enabled ? 'enabled' : 'disabled'}`}>
                    {rule.enabled ? 'Aktivno' : 'Neaktivno'}
                </span>
                <h4>{rule.name}</h4>
            </div>

            <div className="rule-details">
                <p><strong>Tip:</strong> {typeLabels[rule.type] || rule.type}</p>
                <p>
                    <strong>Period aktivacije:</strong>{' '}
                    <span className="active-period-badge">{activePeriodInfo.label}</span>
                    {activePeriodInfo.description && (
                        <span className="active-period-desc"> - {activePeriodInfo.description}</span>
                    )}
                </p>
                {rule.lastExecuted && (
                    <p><strong>Poslednje izvrsenje:</strong> {new Date(rule.lastExecuted.toDate()).toLocaleDateString('sr-RS')}</p>
                )}
            </div>

            {/* Targeting Criteria */}
            <div className="rule-targeting">
                <div className="targeting-header">
                    <strong>Kriterijum ciljanja:</strong>
                    <Button
                        variant="link"
                        size="sm"
                        className="show-query-btn"
                        onClick={() => setShowQuery(!showQuery)}
                    >
                        {showQuery ? 'Sakrij query' : 'Prikazi query'}
                    </Button>
                </div>
                <p className="targeting-criteria">{targetingInfo.criteria}</p>
                <p className="targeting-description">{targetingInfo.description}</p>
                {targetingInfo.cooldown && (
                    <p className="targeting-cooldown">
                        <span className="cooldown-icon">🔄</span> Minimalni razmak: <strong>{targetingInfo.cooldown}</strong>
                    </p>
                )}

                {showQuery && (
                    <pre className="targeting-query">{targetingInfo.query}</pre>
                )}
            </div>

            {/* Template info */}
            {rule.template && rule.template.offerValue && (
                <div className="rule-template">
                    <strong>Poruka koja se salje:</strong>
                    <p>{rule.template.offerValue}</p>
                </div>
            )}

            <div className="rule-actions">
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={onEdit}
                >
                    Izmeni
                </Button>
                <Button
                    variant={rule.enabled ? "outline-danger" : "outline-success"}
                    size="sm"
                    onClick={onToggle}
                >
                    {rule.enabled ? 'Iskljuci' : 'Ukljuci'}
                </Button>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={onDelete}
                >
                    Obrisi
                </Button>
            </div>
        </div>
    );
};

const condition = authUser => !!authUser;
const AIAdvisorContent = withFirebase(AIAdvisorContentBase);

export default withAuthorization(condition)(AIAdvisorPage);
